import withStyles from "isomorphic-style-loader/withStyles";
import { connect } from "react-redux";
import { firebaseConnect } from "react-redux-firebase";
import {
  Col,
  Row,
  InputGroup,
  InputGroupText,
  Label,
  Button,
} from "reactstrap";
import s from "./styles.scss";
import DatePicker from "react-datepicker";
import datePickerS from "react-datepicker/dist/react-datepicker.css";
import React, { useEffect, useState } from "react";
import get from "lodash.get";
import MetricsBubble from "../Superadmin/metricsBubble";
import {
  ModalProps,
  serviceCaller,
  ServiceCallerProps,
  UserCharitiesProps,
  withSuperadminLoginRedirect,
  withUserCharities,
  withModals,
} from "../../composition";
import AsyncSelect from "react-select/async";
import { brandSelectTheme } from "../../../toolkit/baseSelect/theme";
import * as commonActions from "../../../utils/actions/common";
import IAdminMetrics from "../../../types/services/IAdminMetrics";
import Fragment from "../../../toolkit/fragment";
import moment, { Moment } from "moment";
import { getSuperadminMetrics, getAdminMetrics } from "../../../utils/fetch";
import { ScaleLoader } from "react-spinners";
import { ExportType } from "../exportModal";
import { commaSeparated, commaSeparatedInt } from "../../../utils/helpers";
import { isCurrentUserAdmin } from "../../../utils/roleCheck";
import NotFoundPage from "../../../toolkit/notFound";
import LlmChatPrompt from "../llmChatPrompt";

interface Props extends ServiceCallerProps, UserCharitiesProps, ModalProps {
  hideSelection: () => void;
  hasDonations: boolean;
  enableAdminMetrics: boolean;
  superAdminMetrics: boolean;
}

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function Metrics(props: Props) {
  const [metrics, setMetrics] = useState<IAdminMetrics>({} as IAdminMetrics);
  const [fromDate, setFrom] = useState<Moment>(null);
  const [toDate, setTo] = useState<Moment>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCharities, setSelectedCharities] = useState([]);
  const [selectCharities, setSelectCharities] = useState([]);

  useEffect(() => {
    props.hideSelection();
  }, []);

  useEffect(() => {
    if (props.config && props.config.searchServiceUrl) getValues();
  }, [props.config, props.superAdminMetrics, props.currentCharity]);

  useEffect(() => {
    let selectCharitiesArray = [];
    props.charities.forEach(charity => {
      selectCharitiesArray.push({
        label: charity.name,
        value: charity.id,
      });
    });
    setSelectCharities(selectCharitiesArray);
  }, [props.charities]);

  const getFromMillis = (): number | null =>
    fromDate
      ? moment(fromDate)
          .startOf("day")
          .toDate()
          .getTime()
      : null;

  const getToMillis = (): number | null =>
    toDate
      ? moment(toDate)
          .endOf("day")
          .toDate()
          .getTime()
      : null;

  const execExport = () => {
    if (props.superAdminMetrics) {
      const exportAmount =
        selectedCharities.length == 0
          ? selectCharities.length
          : selectedCharities.length;
      props.toggleExportModal(
        ExportType.metricsSuperadminAggregation,
        selectedCharities.map(charity => charity.value),
        exportAmount.toString(),
        getFromMillis(),
        getToMillis()
      );
    } else {
      props.toggleExportModal(
        ExportType.metricsAdminAggregation,
        [props.currentCharity.id],
        null,
        getFromMillis(),
        getToMillis()
      );
    }
  };

  const getValues = () => {
    let fromMillis = getFromMillis();
    let toMillis = getToMillis();
    setLoading(true);
    let result;
    if (props.superAdminMetrics) {
      let ids = selectedCharities.map(charity => charity.value);
      result = getSuperadminMetrics(
        props.config.searchServiceUrl,
        ids,
        fromMillis,
        toMillis,
        props.serviceAccessToken
      );
    } else {
      result = getAdminMetrics(
        props.config.searchServiceUrl,
        props.currentCharity.id,
        fromMillis,
        toMillis,
        props.serviceAccessToken
      );
    }
    result.then(result => {
      setMetrics(result);
      setLoading(false);
    });
  };

  const getBubbles = (type: string) => {
    let data;
    let render = [];
    if (type === "medium") {
      data = getMediumFields();
    } else {
      data = getDonationsFields();
    }
    data.forEach((field, index) => {
      const offset = get(field, "offset", 0);
      render.push(
        <Col xs={12} sm={{ size: 6, offset }} key={`field_${index}`}>
          <MetricsBubble
            title={field["title"]}
            date={field["date"]}
            fields={field["fields"]}
            format={field["format"]}
          />
        </Col>
      );
    });

    return (
      <Fragment>
        <h2>
          {type === "medium" ? "Notification Metrics" : "Donations Metrics"}
        </h2>
        <Row className={s.rowMargin}>{render}</Row>
      </Fragment>
    );
  };

  const filterCharities = text => {
    let data = [];
    let filtered = props.charities.filter(charity =>
      charity.name.toLowerCase().includes(text.toLowerCase())
    );
    filtered.forEach(charity => {
      data.push({
        label: charity.name,
        value: charity.id,
      });
    });
    return Promise.resolve(data);
  };

  const getMediumFields = () => {
    const date = !fromDate && !toDate ? "All Time" : "Custom";
    return [
      {
        title: "Total ViewSparks",
        date: date,
        format: commaSeparatedInt,
        fields: [{ label: "ViewSparks", total: metrics.totalViewSparks }],
      },
      {
        title: "Total Delivered",
        date: date,
        format: commaSeparatedInt,
        fields: [
          { label: "MMS", total: metrics.sms.delivered },
          { label: "Email", total: metrics.email.delivered },
        ],
      },
      {
        title: "Total Sent",
        date: date,
        format: commaSeparatedInt,
        fields: [
          { label: "MMS", total: metrics.sms.totalSent },
          { label: "Email", total: metrics.email.totalSent },
        ],
      },
      {
        title: "Open Rate",
        date: date,
        format: (num: number) => num.toFixed(2) + "%",
        fields: [
          {
            label: "Email",
            total:
              metrics.email.delivered === 0
                ? 0
                : (100 * metrics.email.totalOpened) / metrics.email.delivered,
          },
        ],
      },
      {
        title: "Total Invalids",
        date: date,
        format: commaSeparatedInt,
        fields: [
          { label: "MMS", total: metrics.sms.invalids },
          { label: "Email", total: metrics.email.invalids },
        ],
      },
      {
        title: "Click Rate",
        date: date,
        format: (num: number) => num.toFixed(2) + "%",
        fields: [
          {
            label: "MMS",
            total:
              metrics.sms.delivered === 0
                ? 0
                : (100 * metrics.sms.totalClicks) / metrics.sms.delivered,
          },
          {
            label: "Email",
            total:
              metrics.email.delivered === 0
                ? 0
                : (100 * metrics.email.totalClicks) / metrics.email.totalOpened,
          },
        ],
      },
      {
        title: "Total Unsuscribed",
        date: date,
        format: commaSeparatedInt,
        fields: [
          { label: "MMS", total: metrics.donors.totalUnsubscribedSms },
          { label: "Email", total: metrics.donors.totalUnsubscribedEmail },
        ],
      },
      {
        title: "Unsuscribed Rate",
        date: date,
        format: (num: number) => num.toFixed(2) + "%",
        fields: [
          {
            label: "MMS",
            total:
              metrics.sms.delivered === 0
                ? 0
                : (100 * metrics.donors.totalUnsubscribedSms) /
                  metrics.sms.delivered,
          },
          {
            label: "Email",
            total:
              metrics.email.delivered === 0
                ? 0
                : (100 * metrics.donors.totalUnsubscribedEmail) /
                  metrics.email.delivered,
          },
        ],
      },
    ];
  };

  const getDonationsFields = () => {
    const date = !fromDate && !toDate ? "All Time" : "Custom";
    return [
      {
        title: "Total Donations Received",
        date: date,
        format: commaSeparatedInt,
        fields: [
          { label: "One Time", total: metrics.donations.totalOneTime },
          { label: "Monthly", total: metrics.donations.totalMonthly },
        ],
      },
      {
        title: "Total Amount Received",
        date: date,
        format: formatter.format,
        fields: [
          {
            label: "Amount Donated",
            total: metrics.donations.totalAmountDonated,
          },
        ],
      },
      {
        title: "Total Donations by Medium",
        date: date,
        format: commaSeparatedInt,
        fields: [
          { label: "MMS", total: metrics.donations.totalMediumSms },
          { label: "Email", total: metrics.donations.totalMediumEmail },
          { label: "General", total: metrics.donations.totalMediumOther },
        ],
      },
      {
        title: "Total Donation Amount by Medium",
        date: date,
        format: formatter.format,
        fields: [
          { label: "MMS", total: metrics.donations.totalAmountMediumSms },
          { label: "Email", total: metrics.donations.totalAmountMediumEmail },
          { label: "General", total: metrics.donations.totalAmountMediumOther },
        ],
      },
      {
        title: "Average Donation Amount",
        date: date,
        format: formatter.format,
        fields: [
          {
            label: "Avg Amount",
            total:
              metrics.donations.totalReceived === 0
                ? 0
                : metrics.donations.totalAmountDonated /
                  metrics.donations.totalReceived,
          },
        ],
      },
      {
        title: "Average Number of Donations per ViewSpark",
        date: date,
        format: commaSeparated,
        fields: [
          {
            label: "Avg Donation/ViewSpark",
            total:
              metrics.totalViewSparks === 0
                ? 0
                : metrics.donations.totalReceived / metrics.totalViewSparks,
          },
        ],
      },
    ].concat(
      fromDate || toDate
        ? []
        : [
            {
              title: "Monthly Donations",
              date: date,
              format: commaSeparatedInt,
              fields: [
                {
                  label: "Monthly Gifts",
                  total: metrics.donations.totalSubscriptions,
                },
              ],
            },
            {
              title: "Monthly Donations Amount",
              date: date,
              format: formatter.format,
              fields: [
                {
                  label: "Monthly Donated Amount",
                  total: metrics.donations.totalSubscriptionsAmount,
                },
              ],
            },
          ]
    );
  };

  const startDateChange = e => {
    let startDate = e;
    let endDate = toDate;
    if (startDate && startDate.isAfter(endDate)) {
      endDate = startDate;
    }
    setFrom(startDate);
    setTo(endDate);
  };

  const endDateChange = e => {
    let startDate = fromDate;
    let endDate = e;
    if (startDate && startDate.isAfter(endDate)) {
      endDate = startDate;
    }
    setFrom(startDate);
    setTo(endDate);
  };

  const noResultMessage = value =>
    value.inputValue.length == 0 ? "Type to Search" : "No Results";

  const hasPermission = (): boolean =>
    props.isSuperadmin ||
    (isCurrentUserAdmin(props.currentCharity) &&
      props.enableAdminMetrics &&
      !props.superAdminMetrics);

  return (
    <Fragment>
      {!hasPermission() && <NotFoundPage />}
      {hasPermission() && (
        <main className={s.main}>
          <div className={s.container}>
            <h1>Metrics Dashboard</h1>
            {props.superAdminMetrics && props.isSuperadmin && (
              <LlmChatPrompt
                config={props.config}
                serviceAccessToken={props.serviceAccessToken}
                charityId={props.currentCharity?.id}
                navigate={props.navigate}
              />
            )}
            <Row className={s.rowMargin}>
              {props.superAdminMetrics && (
                <Col xs={12} className={s.colMargin}>
                  <AsyncSelect
                    id={"charitiesSelect"}
                    isMulti
                    loadOptions={filterCharities}
                    value={selectedCharities}
                    placeholder="All charities"
                    defaultOptions={selectCharities}
                    onChange={selected => setSelectedCharities(selected)}
                    noOptionsMessage={noResultMessage}
                    className={s.select}
                    classNamePrefix={"select"}
                    theme={brandSelectTheme}
                    disabled={loading}
                  />
                </Col>
              )}
              <Col xs={1} className={s.col1Width}>
                <Label className={s.dateLabel}>From</Label>
              </Col>
              <Col xs={3} key={"from"}>
                <InputGroup className={s.noFlex}>
                  <InputGroupText
                    className={s.inputGroup}
                  >
                    <i className={"fa fa-calendar " + s.calendarIcon}>&nbsp;</i>
                  </InputGroupText>
                  <DatePicker
                    id="from"
                    selected={fromDate}
                    selectsStart
                    startDate={fromDate}
                    endDate={toDate}
                    onChange={startDateChange}
                    className={s.text}
                    placeholderText={"mm/dd/yyyy"}
                    autoComplete="off"
                    disabled={loading}
                  />
                  <InputGroupText
                    className={s.inputGroupRight}
                  >
                    <i
                      className={"fa fa-times " + s.closeIcon}
                      onClick={() => setFrom(null)}
                    >
                      &nbsp;
                    </i>
                  </InputGroupText>
                </InputGroup>
              </Col>
              &nbsp;
              <Col xs={1} className={s.col1Width}>
                <Label className={s.dateLabel}>To</Label>
              </Col>
              <Col xs={3} key={"to"}>
                <InputGroup className={s.noFlex}>
                  <InputGroupText
                    className={s.inputGroup}
                  >
                    <i className={"fa fa-calendar " + s.calendarIcon}>&nbsp;</i>
                  </InputGroupText>
                  <DatePicker
                    id="to"
                    selected={toDate}
                    selectsStart
                    startDate={toDate}
                    endDate={fromDate}
                    onChange={endDateChange}
                    className={s.text}
                    placeholderText={"mm/dd/yyyy"}
                    autoComplete="off"
                    disabled={loading}
                  />
                  <InputGroupText
                    className={s.inputGroupRight}
                  >
                    <i
                      className={"fa fa-times " + s.closeIcon}
                      onClick={() => setTo(null)}
                    >
                      &nbsp;
                    </i>
                  </InputGroupText>
                </InputGroup>
              </Col>
              <Col xs={{ size: 3, offset: 1 }}>
                <Button
                  id={"exportMetricsButton"}
                  disabled={loading}
                  className={s.button}
                  onClick={execExport}
                >
                  Export
                </Button>
                &nbsp;
                <Button
                  id={"searchMetricsButton"}
                  disabled={loading}
                  className={s.button}
                  onClick={getValues}
                >
                  Search
                </Button>
              </Col>
            </Row>
            <div className={s.centered}>
              <ScaleLoader color={"#1f65b1"} loading={loading} />
            </div>
            {!loading && (
              <Fragment>
                {getBubbles("medium")}
                {(props.superAdminMetrics || props.hasDonations) &&
                  getBubbles("donations")}
              </Fragment>
            )}
          </div>
        </main>
      )}
    </Fragment>
  );
}

const mapStateToProps = state => {
  const { firebase, user } = state;
  const config = `data.${user.currentCharity.id}.config`;
  const customDonationUrl = get(firebase, `${config}.customDonationUrl`, null);
  const enableAdminMetrics = get(
    firebase,
    `${config}.enableAdminMetrics`,
    false
  );
  return { hasDonations: customDonationUrl === null, enableAdminMetrics };
};

const mapDispatchToProps = dispatch => ({
  hideSelection: () => {
    dispatch(commonActions.hideSelection());
  },
});

export default serviceCaller(
  withSuperadminLoginRedirect(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(
      firebaseConnect((_, store) => {
        const charityId = store.getState().user.currentCharity?.id;
        if (!charityId) {
          return [];
        }
        return [
          `${charityId}/config/customDonationUrl`,
          `${charityId}/config/enableAdminMetrics`,
        ];
      })(withUserCharities(withModals(withStyles(s, datePickerS)(Metrics))))
    )
  )
);
